import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { CreateSphere } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { isAiResidentActorId, type TrucksState, type TruckView } from "@townsy/contracts";
import { TRUCK_FLEET, TRUCK_SPEC, arenaGroundHeight, parkedMotion, truckDoorPoint, truckPitch, truckSeatPoint, type TruckMotion } from "@townsy/world";
import type { SeatPose } from "../../scene/actors/actorManager";
import { NAME_TAG_TEXTURE, createNameTagTexture, paintAiNameTag } from "../../scene/signs/signPainter";
import { useTownStore } from "../../state/store";
import type { VenueSceneContext, VenueSceneHandle } from "../types";
import { buildArenaStatic } from "./arenaStatic";
import { localTruck, localTruckPose } from "./localTruck";
import { RemoteTrack, TELEPORT_METRES } from "./remoteTrack";
import { asTrucksState, motionOf } from "./state";
import { buildTruckModel, buildWheelMesh, WHEEL_X } from "./truckModel";

const DUST_POOL = 40;
const R = TRUCK_SPEC.wheelRadius;
/** AI drivers' name tags (world metres): bigger than a walker's, they are read across the track. */
const AI_TAG_HEIGHT = 0.95;

interface AiTag {
  mesh: Mesh;
  texture: DynamicTexture;
  material: StandardMaterial;
  label: string;
}

interface DrawnTruck {
  id: string;
  model: ReturnType<typeof buildTruckModel>;
  /** Played back on the truck's step count (ADR-0032). */
  track: RemoteTrack;
  view: TruckView | null;
  /** The AI driver's name tag (made the first time an AI drives this truck, then reused). */
  tag: AiTag | null;
  pose: TruckMotion;
  pitch: number;
  wheelAngle: number;
  visible: number;
  wasAirborne: boolean;
  dustAt: number;
}

interface Puff {
  mesh: Mesh;
  life: number;
  max: number;
  vx: number;
  vy: number;
  vz: number;
  s0: number;
  s1: number;
}

/**
 * The arena's 3D (ADR-0016): the static arena, six monster trucks drawn from the shared state (other trucks played
 * back evenly on their step count, ADR-0032; the local driver's truck from its prediction's even timeline), airborne
 * trucks at their height with a little pitch, spinning wheels, dust puffs (not under reduced motion), the start lights
 * following the race, an "AI" name tag over the AI racers' trucks, a fade-in where a truck was put somewhere new (the
 * race grid, its bay), and a ring at the driver's door the visitor can use. Everything is disposed with the room.
 */
export function buildTruckArena3d(ctx: VenueSceneContext): VenueSceneHandle {
  const root = new TransformNode("truckArena", ctx.scene);
  try {
    return buildInto(ctx, root);
  } catch (err) {
    // Never leave half an arena in the room.
    root.dispose(false, true);
    throw err;
  }
}

function buildInto(ctx: VenueSceneContext, root: TransformNode): VenueSceneHandle {
  const { scene } = ctx;
  const vmat = new StandardMaterial("truckArenaVC", scene);
  vmat.diffuseColor = Color3.White();
  vmat.specularColor = new Color3(0.06, 0.06, 0.06);
  vmat.specularPower = 24;
  vmat.ambientColor = Color3.White();
  const stat = buildArenaStatic(scene, root, vmat);

  const wheelProto = buildWheelMesh(scene, "truckWheelProto");
  wheelProto.parent = root;
  wheelProto.material = vmat;
  wheelProto.isVisible = false;
  wheelProto.receiveShadows = true;

  const trucks: DrawnTruck[] = TRUCK_FLEET.map((t) => {
    const model = buildTruckModel(scene, t.id, t.number, t.paint, wheelProto, vmat);
    model.root.parent = root;
    const pose = parkedMotion(t);
    return { id: t.id, model, track: new RemoteTrack(), view: null, tag: null, pose, pitch: 0, wheelAngle: 0, visible: 1, wasAirborne: false, dustAt: 0 };
  });
  const byId = new Map(trucks.map((t) => [t.id, t]));
  const casters: Mesh[] = [...stat.casters, wheelProto];
  for (const t of trucks) casters.push(t.model.meshes[0] as Mesh);
  ctx.addShadowCasters(casters);

  // Start lights.
  const lightMat = (name: string, hex: string, glow: number) => {
    const m = new StandardMaterial(`truckLight_${name}`, scene);
    const c = Color3.FromHexString(hex);
    m.diffuseColor = c.scale(0.3);
    m.emissiveColor = c.scale(glow);
    m.specularColor = new Color3(0.2, 0.2, 0.2);
    return m;
  };
  const lights = { off: lightMat("off", "#2B2E34", 0.15), red: lightMat("red", "#FF2A1F", 1), amber: lightMat("amber", "#FFB21F", 1), green: lightMat("green", "#2BFF5A", 1), white: lightMat("white", "#FFFFFF", 0.9) };
  let lightKey = "";

  // Door ring under the truck the visitor can get into.
  const ringTex = new DynamicTexture("truckDoorRingTex", { width: 128, height: 128 }, scene, true);
  const rctx = ringTex.getContext() as unknown as CanvasRenderingContext2D;
  rctx.clearRect(0, 0, 128, 128);
  const g = rctx.createRadialGradient(64, 64, 6, 64, 64, 63);
  g.addColorStop(0, "rgba(242,194,48,0.0)");
  g.addColorStop(0.6, "rgba(242,194,48,0.35)");
  g.addColorStop(0.8, "rgba(255,250,230,0.95)");
  g.addColorStop(0.88, "rgba(242,194,48,0.8)");
  g.addColorStop(1, "rgba(242,194,48,0.0)");
  rctx.fillStyle = g;
  rctx.fillRect(0, 0, 128, 128);
  ringTex.hasAlpha = true;
  ringTex.update();
  const ringMat = new StandardMaterial("truckDoorRingMat", scene);
  ringMat.disableLighting = true;
  ringMat.emissiveTexture = ringTex;
  ringMat.opacityTexture = ringTex;
  ringMat.diffuseColor = Color3.Black();
  ringMat.zOffset = -2;
  const ring = CreateGround("truckDoorRing", { width: 1.8, height: 1.8 }, scene);
  ring.material = ringMat;
  ring.isPickable = false;
  ring.parent = root;
  ring.setEnabled(false);

  // Dust puffs (a small pool, reused).
  const dustMat = new StandardMaterial("truckDustMat", scene);
  dustMat.diffuseColor = Color3.FromHexString("#C9A77C");
  dustMat.emissiveColor = Color3.FromHexString("#6E5A40");
  dustMat.specularColor = Color3.Black();
  const puffs: Puff[] = [];
  for (let i = 0; i < DUST_POOL; i++) {
    const mesh = CreateSphere(`truckDust${i}`, { diameter: 1, segments: 4 }, scene);
    mesh.material = dustMat;
    mesh.isPickable = false;
    mesh.parent = root;
    mesh.setEnabled(false);
    puffs.push({ mesh, life: 0, max: 1, vx: 0, vy: 0, vz: 0, s0: 1, s1: 1 });
  }
  let puffCursor = 0;
  const emit = (x: number, y: number, z: number, vx: number, vz: number, big: boolean) => {
    const p = puffs[puffCursor]!;
    puffCursor = (puffCursor + 1) % puffs.length;
    p.life = 0;
    p.max = big ? 1.1 : 0.8;
    p.vx = vx;
    p.vz = vz;
    p.vy = big ? 1.2 : 0.7;
    p.s0 = big ? 1.0 : 0.6;
    p.s1 = big ? 3.2 : 2.0;
    p.mesh.position.set(x, y + 0.4, z);
    p.mesh.scaling.setAll(p.s0);
    p.mesh.visibility = 0.5;
    p.mesh.setEnabled(true);
  };

  let latest: TrucksState | null = null;
  /** local (performance.now) - server time, from the least-delayed state (relaxes slowly so clock drift is followed). */
  let offset: number | null = null;

  const drawnLocal = (): DrawnTruck | null => {
    const s = localTruck.get();
    return s ? (byId.get(s.vehicleId) ?? null) : null;
  };

  const apply = (t: DrawnTruck, m: TruckMotion, dt: number, reduced: boolean, now: number) => {
    // Put somewhere new (the race grid, back in its bay): fade in there instead of popping up.
    if (Math.hypot(m.x - t.pose.x, m.z - t.pose.z) > TELEPORT_METRES) {
      t.visible = 0;
      t.wasAirborne = m.airborne;
    }
    t.pose = m;
    const root = t.model.root;
    root.position.set(m.x, m.y, m.z);
    root.rotation.y = m.heading;
    const want = truckPitch(m);
    t.pitch += (want - t.pitch) * (1 - Math.exp(-dt * 10));
    t.model.body.rotation.x = -t.pitch;
    if (!reduced) {
      t.wheelAngle = (t.wheelAngle + (m.speed * dt) / R) % (Math.PI * 2);
      for (const w of t.model.wheels) w.rotation.x = t.wheelAngle;
    }
    // Dust: rear wheels on the dirt at speed, and a burst on landing.
    if (!reduced) {
      const onDirt = !m.airborne && m.y < 0.05 && arenaGroundHeight(m.x, m.z) === 0;
      const fx = Math.sin(m.heading);
      const fz = Math.cos(m.heading);
      if (onDirt && Math.abs(m.speed) > 5 && now - t.dustAt > 90) {
        t.dustAt = now;
        const side = Math.random() < 0.5 ? -1 : 1;
        const rx = Math.cos(m.heading) * WHEEL_X * side;
        const rz = -Math.sin(m.heading) * WHEEL_X * side;
        emit(m.x - fx * (TRUCK_SPEC.wheelbase / 2 + 0.6) + rx, m.y, m.z - fz * (TRUCK_SPEC.wheelbase / 2 + 0.6) + rz, -fx * 1.5, -fz * 1.5, false);
      }
      if (t.wasAirborne && !m.airborne) {
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * Math.PI * 2;
          emit(m.x + Math.cos(a) * 1.6, m.y, m.z + Math.sin(a) * 2.2, Math.cos(a) * 2.5, Math.sin(a) * 2.5, true);
        }
      }
    }
    t.wasAirborne = m.airborne;
  };

  const updateLights = (serverNow: number) => {
    const race = latest?.race;
    let mats: StandardMaterial[];
    if (!race || race.phase === "idle") mats = Array(5).fill(lights.off);
    else if (race.phase === "lobby") mats = Array(5).fill(lights.amber);
    else if (race.phase === "countdown") {
      // 3-2-1 as on the overlay: two red lamps at "3", four at "2", all five at "1", then all green at GO.
      const left = Math.max(1, Math.min(3, Math.ceil(((race.endsAt ?? serverNow) - serverNow) / 1000)));
      const lit = left === 3 ? 2 : left === 2 ? 4 : 5;
      mats = Array.from({ length: 5 }, (_, i) => (i < lit ? lights.red : lights.off));
    } else if (race.phase === "racing") mats = Array(5).fill(lights.green);
    else mats = Array(5).fill(lights.white);
    const key = mats.map((m) => m.name).join(",");
    if (key === lightKey) return;
    lightKey = key;
    stat.startLights.forEach((l, i) => (l.material = mats[i]!));
  };

  /** The AI racer driving truck `t` (label), or null when a person drives it or nobody does. */
  const aiLabel = (t: DrawnTruck): string | null => {
    const v = t.view;
    if (!v || v.mode !== "driven" || !isAiResidentActorId(v.driverActorId)) return null;
    return latest?.race.racers.find((r) => r.actorId === v.driverActorId)?.label ?? null;
  };

  const showAiTag = (t: DrawnTruck, label: string | null) => {
    if (label === null) {
      if (t.tag && t.tag.mesh.isEnabled()) t.tag.mesh.setEnabled(false);
      return;
    }
    if (!t.tag) {
      const { texture } = createNameTagTexture(scene, `truckAiTag_${t.id}`, label, "", true);
      const material = new StandardMaterial(`truckAiTagMat_${t.id}`, scene);
      material.disableLighting = true;
      material.emissiveColor = Color3.Black();
      material.diffuseColor = Color3.Black();
      material.specularColor = Color3.Black();
      material.emissiveTexture = texture;
      material.opacityTexture = texture;
      material.backFaceCulling = false;
      const mesh = CreatePlane(`truckAiTag_${t.id}`, { width: (AI_TAG_HEIGHT * NAME_TAG_TEXTURE.width) / NAME_TAG_TEXTURE.height, height: AI_TAG_HEIGHT }, scene);
      mesh.material = material;
      mesh.billboardMode = TransformNode.BILLBOARDMODE_ALL;
      mesh.isPickable = false;
      mesh.parent = root;
      t.tag = { mesh, texture, material, label };
    } else if (t.tag.label !== label) {
      paintAiNameTag(t.tag.texture, label);
      t.tag.label = label;
    }
    const m = t.pose;
    t.tag.mesh.position.set(m.x, m.y + TRUCK_SPEC.roofY + 1.1, m.z);
    t.tag.mesh.visibility = t.visible;
    const enabled = t.visible > 0.01;
    if (t.tag.mesh.isEnabled() !== enabled) t.tag.mesh.setEnabled(enabled);
  };

  return {
    update(state) {
      const st = asTrucksState(state);
      if (!st) return;
      latest = st;
      const local = performance.now();
      const o = local - st.now;
      offset = offset === null ? o : Math.min(o, offset + 2);
      for (const v of st.trucks) {
        const t = byId.get(v.id);
        if (!t) continue;
        t.view = v;
        t.track.push(v.steps, motionOf(v));
      }
    },
    frame(dtMs, nowMs) {
      const dt = Math.min(0.1, dtMs / 1000);
      const reduced = useTownStore.getState().ui.reducedMotion;
      const mine = drawnLocal();
      for (const t of trucks) {
        // Every track keeps playing (a truck someone gets out of continues from where it is drawn).
        const played = t.track.advance(dtMs);
        const m = t === mine ? localTruckPose(nowMs) : played;
        if (m) apply(t, m, dt, reduced, nowMs);
        // Towed trucks fade out; they come back in their bay.
        const hidden = t !== mine && t.view?.mode === "returning";
        const target = hidden ? 0 : 1;
        t.visible += (target - t.visible) * Math.min(1, dt * 6);
        if (Math.abs(t.visible - target) < 0.02) t.visible = target;
        const enabled = t.visible > 0.01;
        if (t.model.root.isEnabled() !== enabled) t.model.root.setEnabled(enabled);
        for (const mesh of t.model.meshes) mesh.visibility = t.visible;
        showAiTag(t, aiLabel(t));
      }
      updateLights(nowMs - (offset ?? nowMs));
      // The ring at the door of the truck the visitor stands at.
      const candidate = useTownStore.getState().ui.vehicleCandidateId;
      const ct = candidate ? byId.get(candidate) : undefined;
      if (ct) {
        const d = truckDoorPoint(ct.pose);
        ring.position.set(d.x, 0.03, d.z);
        if (!ring.isEnabled()) ring.setEnabled(true);
      } else if (ring.isEnabled()) ring.setEnabled(false);
      for (const p of puffs) {
        if (!p.mesh.isEnabled()) continue;
        p.life += dt;
        const f = p.life / p.max;
        if (f >= 1) {
          p.mesh.setEnabled(false);
          continue;
        }
        p.mesh.position.x += p.vx * dt;
        p.mesh.position.y += p.vy * dt;
        p.mesh.position.z += p.vz * dt;
        p.mesh.scaling.setAll(p.s0 + (p.s1 - p.s0) * f);
        p.mesh.visibility = 0.5 * (1 - f);
      }
    },
    seatFor(actorId: string): SeatPose | null {
      let t: DrawnTruck | null = null;
      const mine = drawnLocal();
      if (mine && actorId === ctx.localActorId()) t = mine;
      else t = trucks.find((x) => x.view?.driverActorId === actorId && x.view.mode === "driven") ?? null;
      if (!t) return null;
      const p = truckSeatPoint(t.pose);
      return { x: p.x, y: t.pose.y + 1.6, z: p.z, facing: t.pose.heading, tagHeight: TRUCK_SPEC.roofY + 0.45 - 1.6 };
    },
    followPose() {
      const mine = drawnLocal();
      if (!mine) return null;
      const m = mine.pose;
      return { x: m.x, z: m.z, heading: m.heading, speed: m.speed, lift: 2.3 + m.y };
    },
    dispose() {
      for (const t of trucks) {
        t.model.dispose();
        if (t.tag) {
          t.tag.mesh.dispose();
          t.tag.material.dispose();
          t.tag.texture.dispose();
        }
      }
      wheelProto.dispose();
      stat.dispose();
      for (const p of puffs) p.mesh.dispose();
      dustMat.dispose();
      ring.dispose();
      ringMat.dispose();
      ringTex.dispose();
      for (const m of Object.values(lights)) m.dispose();
      vmat.dispose();
      root.dispose();
    },
  };
}
